import { Message } from '@app/components/Chats/Chat/types';
import { CHANNEL } from '@app/constants/message';
import { IGNORED_INTERNAL_SOURCES } from '@app/constants/messages';

import { LOWEST_PRIORITY_SCORE } from '../constants/general';
import {
  NEEDS_ATTENTION,
  FEATURE_FLAG_TOTAL_LOSS_ASSIST,
  NEEDS_ACTION,
  NOTES_ACCESS,
  CASE_VISIBILITY,
  INTEGRATION_CONFIG_VERISK_ENABLED,
  FEATURE_FLAG_ENABLE_FABRIC_AGENTS,
} from '../constants/permissions';

export const TAG_TYPES = {
  needsAttention: {
    id: 'needs-attention',
    label: 'needs attention',
    sortRanking: 1,
    permission: NEEDS_ATTENTION,
  },
  agentFinished: {
    id: 'agent-finished',
    label: 'assistant finished',
    sortRanking: 2,
    permission: FEATURE_FLAG_ENABLE_FABRIC_AGENTS,
  },
  hasQuestion: {
    id: 'has-question',
    label: 'has question',
    sortRanking: 3,
    permission: NEEDS_ACTION,
  },
  hasUnaddressedMessage: {
    id: 'has-unaddressed-message',
    label: 'has unaddressed message',
    sortRanking: 4,
  },
  hasUnreadMessages: {
    id: 'has-unread-messages',
    label: 'has unread messages',
    sortRanking: 4,
  },
  releaseIssue: {
    id: 'release-issue',
    label: 'release issue',
    sortRanking: 5,
    permission: FEATURE_FLAG_TOTAL_LOSS_ASSIST,
  },
  newNoteMention: {
    id: 'my-note-mentions-and-replies',
    label: 'my note mentions and replies',
    sortRanking: 6,
    permission: NOTES_ACCESS,
  },
  totalLoss: {
    id: 'total-loss',
    label: 'total loss',
    sortRanking: 7,
    permission: FEATURE_FLAG_TOTAL_LOSS_ASSIST,
  },
  optedOut: {
    id: 'opted-out',
    label: 'opted out',
    sortRanking: 8,
  },
  notOptedIn: {
    id: 'not-opted-in',
    label: 'not opted in',
    sortRanking: 9,
  },
  lockedOut: {
    id: 'locked-out',
    label: 'locked out',
    sortRanking: 10,
  },
  failedToSend: {
    id: 'failed-to-send',
    label: 'failed to send',
    sortRanking: LOWEST_PRIORITY_SCORE,
  },
  private: {
    id: 'private',
    label: 'private',
    sortRanking: LOWEST_PRIORITY_SCORE,
    permission: CASE_VISIBILITY,
  },
  verisk: {
    id: 'verisk',
    label: 'verisk',
    sortRanking: LOWEST_PRIORITY_SCORE,
    permission: INTEGRATION_CONFIG_VERISK_ENABLED,
  },
};

/**
 * Unread messages we want to allow to count are:
 *  - all inbound messages
 *  - outbound messages sent from other operators
 *      (not from the user making the request and not from our system)
 *
 *  In regards to outbound AI messages, we want to notify the requesting user
 *  of an unread message for the edge case where an automated message sends
 *  for another operator on the chat. We do not want to be notified of our away
 *  messages, but we are interested in other operators' away messages on the chat.
 *
 *  The field 'isAI' is not used in the logic here since away and OOO messages
 *  are AI, but send on behalf of the logged in user. We want to track outbound
 *  messages, sent by other operators, but not sent by "Marley" (IE, the welcome
 *  messages). The welcome messages do not have authorIds which is why we need
 *  to check for the existence of the field authorId.
 *
 *  When `ignoreInternalUnreadMessages` is true, messages coming from an internal
 *  user (message source is OPERATOR or OPERATOR_AUTO_REPLY) are not marked as
 *  unread, unless the case has never been viewed by the user (`isNotViewedByUser`
 *  is true), in which case the message is still applicable.
 */
interface UnreadMessageOptions {
  ignoreInternalUnreadMessages?: boolean;
  includeActionableEvents?: boolean;
}

type UnreadMessageCandidate =
  | Message
  | {
      authorId: string | { _id: string };
      author?: { id: string };
      isInbound: boolean;
      source?: string;
      isEvent?: boolean;
      actionable?: boolean;
      channel?: string;
    };

export const isApplicableUnreadMessage = (
  message: UnreadMessageCandidate,
  userId?: string,
  isNotViewedByUser = false,
  options: UnreadMessageOptions = {},
) => {
  const {
    ignoreInternalUnreadMessages = false,
    includeActionableEvents = false,
  } = options;

  // Voice events (e.g. voicemails) are not "grey events" — they should
  // continue to follow normal inbound/outbound unread logic below.
  const isGreyEvent = !!message?.isEvent && message?.channel !== CHANNEL.VOICE;

  // Actionable grey event messages count as unread when feature is enabled
  if (isGreyEvent && includeActionableEvents && message?.actionable) {
    return true;
  }

  // Non-actionable grey event messages never count as unread
  if (isGreyEvent) {
    return false;
  }

  if (
    ignoreInternalUnreadMessages &&
    message?.source &&
    IGNORED_INTERNAL_SOURCES.includes(message.source)
  ) {
    return isNotViewedByUser;
  }

  let authorId =
    typeof message?.authorId === 'string'
      ? message?.authorId
      : message?.authorId?._id;

  if (!authorId) {
    authorId = message?.author?.id;
  }

  return (
    message?.isInbound ||
    (!message?.isInbound && // outbound
      typeof authorId !== 'undefined' && // not sent on behalf of the "Marley system"
      authorId !== userId) || // it is not sent on behalf of the requesting user
    isNotViewedByUser // if the chat has not been viewed
  );
};
